/**
 * Cross-tree control channel for the app-shell Care Delivery AI chat.
 *
 * The chat docks once at the app-shell level ({@link AppContent}) and owns its
 * thread state via `useChatTabs`. Other parts of the tree (e.g. the Sources
 * subtab after an auto-ingest) need to ask the chat to surface the pending
 * ingestion-review thread inline — without prop-drilling through DocumentsTab /
 * SourcesPanel. This context exposes that single imperative action.
 *
 * {@link AppContent} provides it; the chat registers the implementation. The
 * default is a no-op so consumers outside a provider still compile and run.
 * Nothing is logged (HIPAA).
 */
import { createContext, useContext } from "react";

/** The imperative chat-control surface exposed to descendants. */
export interface ChatControl {
  /**
   * Open (or refresh + activate) the patient's pending ingestion-review thread
   * in the chat, so its inline diff review renders. A no-op when none is found.
   */
  openPendingReview: () => void;
}

/** A no-op control used as the default (outside a provider). */
const NOOP_CONTROL: ChatControl = { openPendingReview: () => undefined };

/** Context carrying the chat-control surface. */
export const ChatControlContext = createContext<ChatControl>(NOOP_CONTROL);

/**
 * Consume the chat-control surface.
 *
 * @returns The {@link ChatControl} from the nearest provider (or a no-op).
 */
export function useChatControl(): ChatControl {
  return useContext(ChatControlContext);
}
